import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import { IOfficialDiaryPagedResponse } from '../../../../model/sdp/official-daily/response/official-diary-paged-response';
import { dataGridDefaults } from '../../../../components/list-grid-panel';
import { RowAction } from '../../../../components/row-action';
import { TwoLineCell } from '../../../../components/two-line-cell';
import { formatDate } from '../../../../utils/date';

export interface IOfficialDiaryDataTableProps {
    rows: IOfficialDiaryPagedResponse[];
    rowCount: number;
    loading: boolean;
    page: number;
    pageSize: number;
    onPaginationChange: (page: number, pageSize: number) => void;
    onDetail: (row: IOfficialDiaryPagedResponse) => void;
    onChangeVisibility: (row: IOfficialDiaryPagedResponse) => void;
    onCloseEdition: (row: IOfficialDiaryPagedResponse) => void;
}

function YesNoChip({ value, color }: { value: boolean; color: 'success' | 'info' }) {
    return <Chip size="small" label={value ? 'Sim' : 'Não'} color={value ? color : 'default'} variant={value ? 'filled' : 'outlined'} />;
}

export default function OfficialDiaryDataTable(props: IOfficialDiaryDataTableProps) {
    const columns: GridColDef<IOfficialDiaryPagedResponse>[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'editionNumber', headerName: 'Edição', width: 160, sortable: false,
            renderCell: (params) => (
                <TwoLineCell
                    primary={`${params.row.editionNumber}/${params.row.yearPublication}`}
                    secondary={params.row.publicationDate ? `Publicada em ${formatDate(params.row.publicationDate)}` : undefined}
                    bold
                />
            )
        },
        { field: 'description', headerName: 'Descrição', flex: 1, minWidth: 220, sortable: false },
        { field: 'clientName', headerName: 'Entidade', flex: 1, minWidth: 200, sortable: false },
        {
            field: 'isClosed', headerName: 'Fechada', width: 120, sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                    <YesNoChip value={params.row.isClosed} color="success" />
                    {params.row.closedOn && <Typography variant="caption" color="text.secondary">em {formatDate(params.row.closedOn)}</Typography>}
                </Box>
            )
        },
        {
            field: 'isVisibility', headerName: 'Visível', width: 100, sortable: false,
            renderCell: (params) => <YesNoChip value={params.row.isVisibility} color="info" />
        },
        {
            field: 'actions', headerName: 'Ações', width: 140, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <RowAction title="Detalhes" onClick={() => props.onDetail(params.row)}>
                        <DescriptionIcon fontSize="small" />
                    </RowAction>
                    <RowAction title={params.row.isVisibility ? 'Tornar invisível' : 'Tornar visível'} onClick={() => props.onChangeVisibility(params.row)}>
                        {params.row.isVisibility ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </RowAction>
                    {!params.row.isClosed && (
                        <RowAction title="Fechar edição" color="warning" onClick={() => props.onCloseEdition(params.row)}>
                            <DoDisturbIcon fontSize="small" />
                        </RowAction>
                    )}
                </React.Fragment>
            )
        }
    ];

    return (
        <DataGrid
            {...dataGridDefaults}
            rows={props.rows}
            columns={columns}
            loading={props.loading}
            rowCount={props.rowCount}
            paginationMode="server"
            paginationModel={{ page: props.page, pageSize: props.pageSize }}
            onPaginationModelChange={(model) => props.onPaginationChange(model.page, model.pageSize)}
            rowHeight={60}
        />
    );
}
